import React, { useState, useEffect } from 'react'
import { Box, Button, useTheme } from '@mui/material'
import { Source, Layer, useMap } from '@vis.gl/react-maplibre'
import { maplibreGroupChildButtonSx } from '../../consts/map-floating-control-sx'
import SvgCustomIcon from '../icons/SvgCustomIcon'

const PolygonDrawerButton = ({ onPolygonComplete, onDrawingStart, onDrawingEnd }) => {
    const theme = useTheme()
    const { current: map } = useMap();
    const [isDrawing, setIsDrawing] = useState(false);
    const [points, setPoints] = useState([]);

    /* ---------------------------
       BUTTON ACTIONS
    ----------------------------*/

    const startDrawing = () => {
        setPoints([]);
        setIsDrawing(true);
        onDrawingStart?.();
    };

    const finishDrawing = () => {
        setIsDrawing(false);
        onDrawingEnd?.();
        if (points.length > 2) {
            onPolygonComplete?.(points);
            setPoints([]);
        }
    };

    const cancelDrawing = () => {
        setIsDrawing(false);
        setPoints([]);
        onDrawingEnd?.();
    };

    useEffect(() => {
        if (!map) return;

        const handleClick = (e) => {
            if (!isDrawing) return;
            const p = [e.lngLat.lng, e.lngLat.lat];
            setPoints((prev) => [...prev, p]);
        };

        map.on("click", handleClick);
        return () => map.off("click", handleClick);

    }, [isDrawing, map]);


    const polygonGeoJSON =
        points.length > 2
            ? {
                type: "Feature",
                geometry: {
                    type: "Polygon",
                    coordinates: [[...points, points[0]]],
                },
            }
            : null;


    const lineGeoJSON =
        points.length > 1
            ? {
                type: "Feature",
                geometry: {
                    type: "LineString",
                    coordinates: points,
                },
            }
            : null;


    const pointGeoJSON =
        points.length > 0
            ? {
                type: "FeatureCollection",
                features: points.map((p, i) => ({
                    type: "Feature",
                    geometry: { type: "Point", coordinates: p },
                    properties: { id: i }
                })),
            }
            : null;

    const floatSx = maplibreGroupChildButtonSx(theme)

    return (
        <>
            <Box sx={{ display: 'contents' }}>
                {isDrawing && (
                    <Button
                        type="button"
                        className="fred-map-toolbar-btn"
                        onClick={cancelDrawing}
                        sx={floatSx}
                    >
                        <SvgCustomIcon
                            name="delete"
                            color="red"
                            size={22}
                        />
                    </Button>
                )}

                <Button
                    type="button"
                    className="fred-map-toolbar-btn"
                    onClick={() => {
                        if (!isDrawing) startDrawing();
                        else finishDrawing();
                    }}
                    sx={floatSx}
                >
                    <SvgCustomIcon
                        name={isDrawing ? 'check' : 'edit'}
                        color={'#425B61'}
                        size={24}
                    />
                </Button>
            </Box>

            {pointGeoJSON && (
                <Source id="poly-points" type="geojson" data={pointGeoJSON}>
                    <Layer
                        id="poly-points-layer"
                        type="circle"
                        paint={{
                            "circle-radius": 4,
                            "circle-color": "#ff5500",
                            "circle-stroke-width": 1,
                            "circle-stroke-color": "#ffffff"
                        }}
                    />
                </Source>
            )}

            {lineGeoJSON && (
                <Source id="poly-temp-line" type="geojson" data={lineGeoJSON}>
                    <Layer
                        id="poly-temp-line-layer"
                        type="line"
                        paint={{
                            "line-color": "#aa4400",
                            "line-width": 2,
                            "line-dasharray": [2, 2]
                        }}
                    />
                </Source>
            )}

            {polygonGeoJSON && (
                <Source id="poly-final" type="geojson" data={polygonGeoJSON}>
                    <Layer
                        id="poly-fill"
                        type="fill"
                        paint={{
                            "fill-color": "#088",
                            "fill-opacity": 0.4,
                        }}
                    />
                    <Layer
                        id="poly-line"
                        type="line"
                        paint={{
                            "line-color": "#044",
                            "line-width": 2,
                        }}
                    />
                </Source>
            )}
        </>
    );
};

export default PolygonDrawerButton;
